import { Button, Textarea } from '@loadsmart/loadsmart-ui'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Stack,
  IconButton,
  LinearProgress,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Divider,
  Chip,
  Alert,
} from '@mui/material'
import {
  Delete,
  Description,
  PictureAsPdf,
  TextFields,
  AutoFixHigh as MagicWand,
  Close,
  CloudDownload,
  Upload,
  ArrowBack,
  ArrowForward,
  Refresh,
  Check,
  Timer,
  Code,
  Image,
  VideoFile,
  AudioFile,
  Archive,
} from '@mui/icons-material'
import { useState, useCallback, useMemo, useEffect, useRef } from 'react'
import { useWizard } from 'atoms/Wizard'
import FileUploader from 'molecules/FileUploader/FileUploader'
import X12CanonicalSpec from './X12CanonicalSpec'
import {
  useX12SchemaGenerationService,
  FileWithSource,
} from '../services/x12SchemaGenerationService'
import { toast } from 'react-toastify'

interface X12SchemaGenerationWizardProps {
  open: boolean
  onClose: () => void
  onAccept: (spec: any) => void
  modelId?: string
}

const CUSTOMIZATION_PLACEHOLDER = `
## Customizations
- S3 loop is nested inside S1 loop
- S302 must have a default value of '1'
- S302 must have a maximum value of '10'
- S302 must have a minimum value of '1'
`

export default function X12SchemaGenerationWizard({
  open,
  onClose,
  onAccept,
  modelId,
}: X12SchemaGenerationWizardProps) {
  const [resourceFiles, setResourceFiles] = useState<FileWithSource[]>([])
  const [customizations, setCustomizations] = useState<string>('')
  const [generatedSpec, setGeneratedSpec] = useState<any>(null)
  const [generationMessage, setGenerationMessage] = useState<string>('')
  const [isLoadingExistingResources, setIsLoadingExistingResources] = useState<boolean>(false)
  const [generationTimer, setGenerationTimer] = useState<number>(0)
  const [isTimerRunning, setIsTimerRunning] = useState<boolean>(false)
  const [generationError, setGenerationError] = useState<string | null>(null)

  // Track if resources have been loaded to prevent multiple calls
  const resourcesLoadedRef = useRef(false)
  const timerIntervalRef = useRef<NodeJS.Timeout | null>(null)

  // Service layer
  const schemaGenerationService = useX12SchemaGenerationService(modelId)

  // Wizard configuration
  const steps = useMemo(
    () => [
      { id: 'resources', label: 'Resources Upload' },
      { id: 'customizations', label: 'Customizations' },
      { id: 'generated-spec', label: 'Generated Spec' },
    ],
    []
  )

  const [wizardState, wizardDispatch] = useWizard({
    steps,
    current: 'resources',
  })

  // Get current step index for MUI Stepper
  const currentStepIndex = useMemo(() => {
    return steps.findIndex(step => step.id === wizardState.current)
  }, [steps, wizardState.current])

  // Memoized expensive operations
  const fileIconCache = useMemo(() => new Map<string, JSX.Element>(), [])

  const getFileIcon = useCallback(
    (fileName: string, contentType?: string) => {
      // If we have a content-type, use it as the primary source for icon selection
      if (contentType) {
        const lowerContentType = contentType.toLowerCase()

        // Map content types to icons
        if (lowerContentType === 'application/pdf') {
          return <PictureAsPdf sx={{ fontSize: 20, color: '#d32f2f' }} />
        }

        if (lowerContentType === 'application/json' || lowerContentType === 'application/xml') {
          return <Code sx={{ fontSize: 20, color: '#388e3c' }} />
        }

        if (
          lowerContentType === 'text/plain' ||
          lowerContentType === 'text/csv' ||
          lowerContentType === 'text/markdown'
        ) {
          return <TextFields sx={{ fontSize: 20, color: '#1976d2' }} />
        }

        if (lowerContentType === 'application/edi-x12' || lowerContentType === 'application/x12') {
          return <Code sx={{ fontSize: 20, color: '#388e3c' }} />
        }

        if (lowerContentType === 'application/yaml' || lowerContentType === 'text/yaml') {
          return <Code sx={{ fontSize: 20, color: '#388e3c' }} />
        }

        if (lowerContentType.startsWith('image/')) {
          return <Image sx={{ fontSize: 20, color: '#f57c00' }} />
        }

        if (lowerContentType.startsWith('video/')) {
          return <VideoFile sx={{ fontSize: 20, color: '#7b1fa2' }} />
        }

        if (lowerContentType.startsWith('audio/')) {
          return <AudioFile sx={{ fontSize: 20, color: '#c2185b' }} />
        }

        if (
          lowerContentType === 'application/zip' ||
          lowerContentType === 'application/x-zip-compressed' ||
          lowerContentType === 'application/x-rar-compressed' ||
          lowerContentType === 'application/x-7z-compressed' ||
          lowerContentType === 'application/x-tar' ||
          lowerContentType === 'application/gzip'
        ) {
          return <Archive sx={{ fontSize: 20, color: '#ff9800' }} />
        }

        if (lowerContentType === 'application/octet-stream') {
          return <Description sx={{ fontSize: 20, color: '#666' }} />
        }
      }

      // Fallback to file extension if no content-type or no match found
      const extension = fileName.split('.').pop()?.toLowerCase()

      // Check cache first
      if (fileIconCache.has(extension || '')) {
        return fileIconCache.get(extension || '')
      }

      let icon: JSX.Element
      switch (extension) {
        case 'pdf':
          icon = <PictureAsPdf sx={{ fontSize: 20, color: '#d32f2f' }} />
          break
        case 'txt':
        case 'doc':
        case 'docx':
          icon = <TextFields sx={{ fontSize: 20, color: '#1976d2' }} />
          break
        case 'json':
        case 'xml':
        case 'yaml':
        case 'yml':
        case 'js':
        case 'css':
          icon = <Code sx={{ fontSize: 20, color: '#388e3c' }} />
          break
        case 'jpg':
        case 'jpeg':
        case 'png':
        case 'gif':
        case 'svg':
          icon = <Image sx={{ fontSize: 20, color: '#f57c00' }} />
          break
        case 'mp4':
        case 'avi':
        case 'mov':
        case 'wmv':
          icon = <VideoFile sx={{ fontSize: 20, color: '#7b1fa2' }} />
          break
        case 'mp3':
        case 'wav':
        case 'aac':
          icon = <AudioFile sx={{ fontSize: 20, color: '#c2185b' }} />
          break
        case 'zip':
        case 'rar':
        case '7z':
        case 'tar':
        case 'gz':
          icon = <Archive sx={{ fontSize: 20, color: '#ff9800' }} />
          break
        default:
          icon = <Description sx={{ fontSize: 20, color: '#666' }} />
      }

      // Cache the result
      fileIconCache.set(extension || '', icon)
      return icon
    },
    [fileIconCache]
  )

  const formatFileSize = useCallback((bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }, [])

  const handleFileUploaded = useCallback((files: File[]) => {
    const newFilesWithSource: FileWithSource[] = files.map(file => ({
      file,
      source: 'uploaded' as const,
    }))
    setResourceFiles(prevFiles => [...prevFiles, ...newFilesWithSource])
  }, [])

  const handleRemoveFile = useCallback((indexToRemove: number) => {
    setResourceFiles(prevFiles => prevFiles.filter((_, index) => index !== indexToRemove))
  }, [])

  // Load existing knowledge resources using service
  const loadExistingKnowledgeResources = useCallback(async () => {
    if (!modelId) return

    setIsLoadingExistingResources(true)
    try {
      const files = await schemaGenerationService.loadExistingKnowledgeResources()

      if (files.length > 0) {
        setResourceFiles(files)
      }

      // Load schema generation rules file
      try {
        const promptContent = await schemaGenerationService.loadSchemaGenerationPrompt()
        if (promptContent) {
          setCustomizations(promptContent)
        }
      } catch (error) {
        console.error('Failed to load schema generation rules:', error)
      }
    } finally {
      setIsLoadingExistingResources(false)
    }
  }, [modelId, schemaGenerationService])

  // Timer effect
  useEffect(() => {
    if (isTimerRunning) {
      timerIntervalRef.current = setInterval(() => {
        setGenerationTimer(prev => prev + 1)
      }, 1000)
    } else {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current)
        timerIntervalRef.current = null
      }
    }

    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current)
        timerIntervalRef.current = null
      }
    }
  }, [isTimerRunning])

  // Format timer to mm:ss
  const formatTimer = useCallback((seconds: number) => {
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`
  }, [])

  // Reset wizard state when it opens to ensure fresh state
  useEffect(() => {
    if (open) {
      // Reset wizard state
      wizardDispatch({ type: 'reset', payload: { steps, current: 'resources' } })
      setResourceFiles([])
      setCustomizations('')
      setGeneratedSpec(null)
      setGenerationMessage('')
      setGenerationError(null)
      setIsLoadingExistingResources(false)
      setGenerationTimer(0)
      setIsTimerRunning(false)
      resourcesLoadedRef.current = false
    }
  }, [open, wizardDispatch, steps])

  // Load existing resources when wizard opens and resources are available
  useEffect(() => {
    if (
      open &&
      modelId &&
      schemaGenerationService.existingResources.length > 0 &&
      resourceFiles.length === 0 &&
      !resourcesLoadedRef.current
    ) {
      resourcesLoadedRef.current = true
      loadExistingKnowledgeResources()
    }
  }, [open, modelId, schemaGenerationService.existingResources, resourceFiles.length])

  const handleNext = useCallback(() => {
    if (wizardState.current === 'resources') {
      if (resourceFiles.length === 0) {
        toast.warning('Please upload at least one resource file before proceeding.')
        return
      }
      wizardDispatch({ type: 'next' })
    } else if (wizardState.current === 'customizations') {
      wizardDispatch({ type: 'next' })
      // Generate the spec when moving to the final step
      handleGenerateSpec()
    }
  }, [wizardState.current, resourceFiles.length, wizardDispatch])

  const handlePrevious = useCallback(() => {
    wizardDispatch({ type: 'previous' })
  }, [wizardDispatch])

  const handleGenerateSpec = useCallback(async () => {
    // Clear any previous errors
    setGenerationError(null)

    // Start timer
    setGenerationTimer(0)
    setIsTimerRunning(true)

    try {
      // Extract just the File objects for the API call
      const files = resourceFiles.map(fws => fws.file)
      const [response, error] = await schemaGenerationService.generateSchema({
        dataModelSchemaId: modelId,
        resourceFiles: files,
        customizations,
      })

      // Check if there was an error
      if (error) {
        const errorMessage = error.message
        console.error('Schema generation error:', errorMessage)
        setGenerationError(errorMessage)
        toast.error(errorMessage)
        return
      }

      // Check if we have a response
      if (!response) {
        const errorMessage = 'No response received from schema generation service'
        console.error('Schema generation error:', errorMessage)
        setGenerationError(errorMessage)
        toast.error(errorMessage)
        return
      }

      // Parse the schema
      try {
        const parsedSchema = JSON.parse(response.schema)
        setGenerationMessage(response.message || 'Schema generated successfully')
        setGeneratedSpec(parsedSchema)
      } catch (parseError) {
        const msg = `Failed to parse schema. Returned schema is not valid JSON: ${response.schema}`
        console.error(msg, parseError)
        setGenerationError(msg)
        toast.error(msg)
      }
    } catch (error) {
      // Fallback error handling for unexpected errors
      const errorMessage = error instanceof Error ? error.message : String(error)
      console.error('Unexpected schema generation error:', errorMessage)
      setGenerationError(errorMessage)
      toast.error(errorMessage)
    } finally {
      // Stop timer
      setIsTimerRunning(false)
    }
  }, [resourceFiles, customizations, schemaGenerationService, modelId])

  const handleAccept = useCallback(async () => {
    if (generatedSpec && modelId) {
      try {
        await schemaGenerationService.acceptGeneratedSchema(modelId, customizations, generatedSpec)
        onAccept(generatedSpec)
        onClose()
      } catch (error) {
        // Error is already handled by the service
      }
    }
  }, [generatedSpec, modelId, customizations, schemaGenerationService, onAccept, onClose])

  const handleClose = useCallback(() => {
    onClose()
  }, [onClose])

  const isGenerating = schemaGenerationService.isGenerating
  const canProceedFromResources = resourceFiles.length > 0
  const canProceedFromCustomizations = true // Always allow proceeding from customizations

  // Separate files by source
  const existingFiles = resourceFiles.filter(fws => fws.source === 'existing')
  const uploadedFiles = resourceFiles.filter(fws => fws.source === 'uploaded')

  const renderFileItem = useCallback(
    (fileWithSource: FileWithSource, index: number) => {
      const { file, source } = fileWithSource
      const isExisting = source === 'existing'

      return (
        <Paper
          key={index}
          elevation={0}
          sx={{
            p: 1.5,
            border: `1px solid ${isExisting ? '#e8f5e8' : '#e0e0e0'}`,
            borderRadius: 1,
            backgroundColor: isExisting ? '#f8fff8' : '#fafafa',
            '&:hover': {
              backgroundColor: isExisting ? '#f0fff0' : '#f5f5f5',
              borderColor: isExisting ? '#4caf50' : '#bdbdbd',
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
              {getFileIcon(file.name, file.type)}
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 500,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {file.name}
                  </Typography>
                  <Chip
                    label={isExisting ? 'Existing' : 'New'}
                    size="small"
                    icon={
                      isExisting ? (
                        <CloudDownload sx={{ fontSize: 14 }} />
                      ) : (
                        <Upload sx={{ fontSize: 14 }} />
                      )
                    }
                    sx={{
                      height: 20,
                      fontSize: '0.7rem',
                      backgroundColor: isExisting ? '#e8f5e8' : '#e3f2fd',
                      color: isExisting ? '#2e7d32' : '#1976d2',
                      '& .MuiChip-icon': {
                        color: 'inherit',
                      },
                    }}
                  />
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {formatFileSize(file.size)}
                </Typography>
              </Box>
            </Box>
            {!isExisting && (
              <IconButton
                size="small"
                onClick={() => handleRemoveFile(index)}
                sx={{
                  color: '#666',
                  '&:hover': {
                    color: '#d32f2f',
                    backgroundColor: 'rgba(211, 47, 47, 0.04)',
                  },
                }}
              >
                <Delete sx={{ fontSize: 16 }} />
              </IconButton>
            )}
          </Box>
        </Paper>
      )
    },
    [getFileIcon, formatFileSize, handleRemoveFile]
  )

  const renderStepContent = () => {
    switch (wizardState.current) {
      case 'resources':
        return (
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Upload Resources
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Upload PDFs, documents, or text files that contain X12 specifications, documentation,
              or examples. The generation process will consider both existing resources and newly
              uploaded files.
            </Typography>

            {/* Loading indicator for existing resources */}
            {isLoadingExistingResources && (
              <Box sx={{ mb: 2 }}>
                <LinearProgress sx={{ mb: 1 }} />
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                  Loading existing resources...
                </Typography>
              </Box>
            )}

            <Stack spacing={2}>
              <FileUploader
                handleUpload={handleFileUploaded}
                accept={['.pdf', '.txt', '.doc', '.docx']}
                multiple={true}
              />

              {resourceFiles.length > 0 && (
                <Stack spacing={2}>
                  {/* Existing Resources Section */}
                  {existingFiles.length > 0 && (
                    <Box>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        sx={{
                          fontWeight: 600,
                          mb: 1,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                        }}
                      >
                        <CloudDownload sx={{ fontSize: 16, color: '#2e7d32' }} />
                        Existing Resources ({existingFiles.length})
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mb: 1.5, display: 'block' }}
                      >
                        These files are already available in your model and will be included in the
                        generation process.
                      </Typography>
                      <Stack spacing={1}>
                        {existingFiles.map((fileWithSource, index) => {
                          const globalIndex = resourceFiles.findIndex(fws => fws === fileWithSource)
                          return renderFileItem(fileWithSource, globalIndex)
                        })}
                      </Stack>
                    </Box>
                  )}

                  {/* Divider between sections */}
                  {existingFiles.length > 0 && uploadedFiles.length > 0 && (
                    <Divider sx={{ my: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        Additional Files
                      </Typography>
                    </Divider>
                  )}

                  {/* Newly Uploaded Files Section */}
                  {uploadedFiles.length > 0 && (
                    <Box>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        sx={{
                          fontWeight: 600,
                          mb: 1,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                        }}
                      >
                        <Upload sx={{ fontSize: 16, color: '#1976d2' }} />
                        Newly Uploaded Files ({uploadedFiles.length})
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mb: 1.5, display: 'block' }}
                      >
                        These files will be added to your model and used in the generation process.
                      </Typography>
                      <Stack spacing={1}>
                        {uploadedFiles.map((fileWithSource, index) => {
                          const globalIndex = resourceFiles.findIndex(fws => fws === fileWithSource)
                          return renderFileItem(fileWithSource, globalIndex)
                        })}
                      </Stack>
                    </Box>
                  )}
                </Stack>
              )}
            </Stack>
          </Box>
        )

      case 'customizations':
        return (
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Define Customizations
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Define specific rules, constraints, or modifications for the X12 schema generation
            </Typography>

            <Box sx={{ width: '100%' }}>
              <Textarea
                rows={15}
                className="w-full"
                placeholder={CUSTOMIZATION_PLACEHOLDER}
                value={customizations}
                onChange={e => setCustomizations(e.target.value)}
              />
            </Box>
          </Box>
        )

      case 'generated-spec':
        return (
          <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6" gutterBottom>
              Generated Specification
            </Typography>

            {isGenerating ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <LinearProgress sx={{ mb: 2 }} />
                <Alert severity="info">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Timer sx={{ fontSize: 20 }} />
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Generation Time: {formatTimer(generationTimer)}
                    </Typography>
                  </Box>
                  <Typography variant="body2">
                    Generating specification, don&apos;t close this dialog. This may take a few
                    minutes, especially if this is the first generation batch for the provided
                    resources. Second generation batch will be faster.
                  </Typography>
                </Alert>
              </Box>
            ) : generatedSpec ? (
              <Stack spacing={2} sx={{ flex: 1, minHeight: 0 }}>
                {/* Generation completion info */}
                <Alert severity="success">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Generation Completed in: {formatTimer(generationTimer)}
                    </Typography>
                  </Box>
                  <Typography variant="body2">
                    {generationMessage && (
                      <Typography variant="body2" color="text.secondary">
                        {generationMessage}
                      </Typography>
                    )}
                    <Divider />
                    <Typography variant="body2">
                      You can review the specification below and accept it when ready.
                    </Typography>
                  </Typography>
                </Alert>

                <X12CanonicalSpec
                  data={generatedSpec}
                  onChange={() => {}} // Read-only in wizard
                  readonly={true}
                />
              </Stack>
            ) : (
              !generationError &&
              !generatedSpec && (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    No specification generated yet.
                  </Typography>
                </Box>
              )
            )}

            {/* Display generation error if any */}
            {generationError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                  Generation Failed
                </Typography>
                <Typography variant="body2">{generationError}</Typography>
              </Alert>
            )}
          </Box>
        )

      default:
        return null
    }
  }

  const renderStepActions = () => {
    const isLastStep = wizardState.current === 'generated-spec'
    const isFirstStep = wizardState.current === 'resources'

    return (
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Stack direction="row" spacing={2} sx={{ width: '100%', justifyContent: 'flex-end' }}>
          <Button variant="secondary" onClick={handleClose} leading={<Close />}>
            Cancel
          </Button>

          {!isFirstStep && !isLastStep && (
            <Button variant="secondary" onClick={handlePrevious} leading={<ArrowBack />}>
              Previous
            </Button>
          )}

          {!isLastStep ? (
            <Button
              variant="primary"
              onClick={handleNext}
              disabled={
                (wizardState.current === 'resources' && !canProceedFromResources) ||
                (wizardState.current === 'customizations' && !canProceedFromCustomizations) ||
                isGenerating
              }
              leading={<ArrowForward />}
            >
              Next
            </Button>
          ) : (
            <>
              {!isFirstStep && (
                <Button variant="secondary" onClick={handlePrevious} leading={<ArrowBack />}>
                  Previous
                </Button>
              )}
              <Button
                variant="secondary"
                onClick={handleGenerateSpec}
                leading={<Refresh />}
                disabled={isGenerating}
              >
                Regenerate
              </Button>
              <Button
                variant="primary"
                onClick={handleAccept}
                disabled={!generatedSpec}
                leading={<Check />}
              >
                Accept
              </Button>
            </>
          )}
        </Stack>
      </DialogActions>
    )
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: { height: '90vh', maxWidth: '85vw' },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MagicWand sx={{ fontSize: 24, color: 'primary.main' }} />
            <Typography variant="h6">Generate X12 Schema</Typography>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
        {/* MUI Stepper */}
        <Box sx={{ px: 3, py: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Stepper activeStep={currentStepIndex} alternativeLabel>
            {steps.map((step, index) => (
              <Step key={step.id}>
                <StepLabel
                  sx={{
                    '& .MuiStepLabel-label': {
                      fontWeight:
                        wizardState.current === step.id ? 'fontWeightBold' : 'fontWeightRegular',
                      color: wizardState.current === step.id ? 'text.primary' : 'text.secondary',
                    },
                  }}
                >
                  {step.label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        {/* Step Content */}
        <Box sx={{ flex: 1, overflow: 'auto' }}>{renderStepContent()}</Box>
      </DialogContent>

      {renderStepActions()}
    </Dialog>
  )
}
